import { Injectable } from '@angular/core';
import { Environment } from '../../../environments/environment';
import { filter, first, ReplaySubject } from 'rxjs';
import { STAGE } from '../enums/stage.enum';
import { KameleoonClient, Environment as KameleoonEnvironment } from '@kameleoon/javascript-sdk';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class KameleoonService {

  public static Goals = {
    DELIVERY_CONFIRMATION: 286607,
    PASSED_PERSONAL_DATA_PAGE: 286606,
    DELIVERY_NEW_HERE: 292967,
    DELIVERY_SITUATION_TO_BANKDATA: 292974,
    DELIVERY_BANKDATA_DONE: 292975
  };
  public kameleoonLoaded$ = new ReplaySubject<KameleoonClient>(1);
  private siteCode: string = Environment.kameleoon?.siteId;
  private kameleoonClient: KameleoonClient | null = null;

  constructor() {
    this.kameleoonLoaded$.next(null);
  }

  public static getExperimentIdsOfRoute(activatedRoute: ActivatedRoute) {
    return activatedRoute?.snapshot?.data['experimentIds'];
  }

  /**
   * get existing visitor code from cookie or create a new one, if no visitorcode exists
   * Note: The new SDK handles visitor codes differently, no need to slice
   */
  obtainVisitorCode(kameleoonClient: KameleoonClient) {
    return kameleoonClient?.getVisitorCode();
  }

  processConversion(goalId: number, revenue?: number) {
    this.kameleoonLoaded$.pipe(
      filter((loaded) => !!loaded),
      first()
    ).subscribe({ next: (kameleoonClient) => {
      try {
        const visitorCode = this.obtainVisitorCode(kameleoonClient);
        kameleoonClient.trackConversion({ visitorCode, goalId, revenue });
      } catch (error) {
        this.logError('processConversion error:', error);
      }
    } });
  }

  /**
   * Load feature flag / experiment
   * @param featureKey Feature key (string)
   * @param callback
   */
  loadFeatureFlag(featureKey: string, callback: (isActive: boolean, variationKey: string, variables: {}) => any) {
    this.kameleoonLoaded$.pipe(
      filter((loaded) => !!loaded),
      first()
    ).subscribe({ next: (kameleoonClient) => {
      try {
        const visitorCode = this.obtainVisitorCode(kameleoonClient);
        const isActive = kameleoonClient.isFeatureFlagActive(visitorCode, featureKey);

        if (isActive) {
          const variation = kameleoonClient.getVariation({
            visitorCode,
            featureKey
          });
          const variables = variation?.variables;

          // Debug display for development
          // if (Environment.stage !== STAGE.Production && variation?.key) {
          //   this.showDebugVariant(variation.key);
          // }

          callback(isActive, variation?.key || 'default', variables);
        } else {
          callback(false, 'reference', {});
        }
      } catch (error) {
        this.logError('loadFeatureFlag error:', error);
        // Fallback to reference variation
        callback(false, 'reference', {});
      }
    } });
  }

  async load() {
    try {
      this.kameleoonClient = new KameleoonClient({
        siteCode: this.siteCode,
        configuration: {
          updateInterval: 5,
          environment: Environment.stage === STAGE.Prod ? KameleoonEnvironment.Production : KameleoonEnvironment.Staging
        }
      });

      await this.kameleoonClient.initialize();
      this.kameleoonLoaded$.next(this.kameleoonClient);
    } catch (error) {
      this.logError('Failed to initialize Kameleoon:', error);
      this.kameleoonLoaded$.next(null);
    }
  }

  unload() {
    if (this.kameleoonClient) {
      // Clean up debug display
      const debugElement = document.querySelector('.kameleoon-debug');
      if (debugElement) {
        debugElement.remove();
      }
    }
    this.kameleoonClient = null;
    this.kameleoonLoaded$.next(null);
  }

  private showDebugVariant(variantName: string) {
    const existingDebug = document.querySelector('.kameleoon-debug');
    if (existingDebug) {
      existingDebug.remove();
    }

    const variantContainer = document.createElement('div');
    variantContainer.innerHTML = variantName;
    variantContainer.className = 'kameleoon-debug';
    variantContainer.setAttribute('style', 'position: fixed; top:5px; left:5px; font-weight: bold;z-index:10000;background-color: rgba(255,255,255,0.9); border: 1px dashed black; padding: 10px 20px; border-radius: 20px; font-size: 12px;');
    document.body.appendChild(variantContainer);
  }

  private logError(...params) {
    if (Environment.stage !== STAGE.Prod) {
      console.error('Kameleoon', ...params);
    }
  }
}
